import { useApiDocumentsV1DocumentsPartialUpdate } from 'api';
import { useNotification } from 'features';
import { Form, TextField, useForm, yup, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { Button, Dialog, DialogActions, DialogContent, DialogHeader } from 'ui';

type FormValues = {
  alias: string;
};

type UpdateAliasDialogProps = {
  open: boolean;
  alias: string | null;
  documentId: number;
  onClose: () => void;
  onSuccess?: () => void;
};

export const UpdateAliasDialog = ({ open, onClose, documentId, alias, onSuccess }: UpdateAliasDialogProps) => {
  const { t } = useTranslation('orders');
  const { open: openNotification } = useNotification();
  const { mutate: updateDocument, isPending } = useApiDocumentsV1DocumentsPartialUpdate({
    mutation: {
      onError: () => {
        openNotification?.({
          message: t('documentGeneration.updateAliasDialog.notifications.failedToUpdateAlias'),
          type: 'error',
        });
      },
      onSuccess: () => {
        onClose();
        onSuccess?.();
        openNotification?.({
          message: t('documentGeneration.updateAliasDialog.notifications.aliasUpdated'),
          type: 'success',
        });
      },
    },
  });

  const schema = yup.object().shape({
    alias: yup
      .string()
      .required(t('documentGeneration.updateAliasDialog.validation.aliasRequired'))
      .max(40, t('documentGeneration.updateAliasDialog.validation.aliasMaxLength')),
  });

  const form = useForm<FormValues>({
    defaultValues: {
      alias: alias ?? '',
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (values: FormValues) => {
    updateDocument({ data: values, id: documentId });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogHeader title={t('documentGeneration.updateAliasDialog.title')} onClose={onClose} />
      <Form {...form} onSubmit={onSubmit}>
        <DialogContent>
          <TextField
            name="alias"
            label={t('documentGeneration.updateAliasDialog.labels.alias')}
            placeholder={t('documentGeneration.updateAliasDialog.placeholders.alias')}
            helperText={t('documentGeneration.updateAliasDialog.helperText.alias')}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={onClose}>
            {t('documentGeneration.updateAliasDialog.actions.cancel')}
          </Button>
          <Button variant="primary" type="submit" loading={isPending}>
            {t('documentGeneration.updateAliasDialog.actions.save')}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
